import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Loader2, Lock, CheckCircle2, AlertTriangle, RefreshCw, Star, Users, Wallet } from 'lucide-react';
import { fmt, gbErr } from './gbAdminUtils';

const STATUS = {
  AWAITING_APPROVAL: { t: 'aguardando sua aprovação', c: 'text-amber-600' },
  PROCESSING: { t: 'em processamento na KAKO', c: 'text-blue-600' },
  FAILED: { t: 'falhou — pode tentar de novo', c: 'text-rose-600' },
  BLOCKED: { t: 'retido (contestação em aberto)', c: 'text-rose-700' },
  PAID: { t: 'creditado', c: 'text-emerald-600' },
};

const KIND_ICON = { level: Star, clan: Users, balance: Wallet };

export default function GbFinalPrizes({ seasonId, seasonStatus }) {
  const [rel, setRel] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/gb/season/${seasonId}/final`);
      setRel(data);
    } catch (e) { toast.error(gbErr(e)); }
  }, [seasonId]);

  useEffect(() => { load(); }, [load]);

  const acao = async (url, body, msg, key) => {
    setBusy(key);
    try { await api.post(url, body); toast.success(msg); await load(); }
    catch (e) { toast.error(gbErr(e)); } finally { setBusy(''); }
  };

  if (!rel) return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin text-orange-500" /></div>;

  const base = `/admin/gb/season/${seasonId}/final`;
  const grupos = rel.payouts || {};

  const Lista = ({ kind }) => {
    const rows = grupos[kind] || [];
    const Icon = KIND_ICON[kind];
    const pend = rows.filter((r) => r.status !== 'PAID');
    return (
      <div className="rounded-xl border p-3" data-testid={`gb-final-${kind}`}>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h4 className="flex items-center gap-1.5 text-sm font-bold text-slate-700">
            <Icon className="h-4 w-4 text-amber-500" />{rel.kinds[kind]}
          </h4>
          <span className="text-[11px] text-slate-500">
            {rows.length} pagamento(s) · {fmt(rel.totals?.[kind]?.coins || 0)} moedas ·
            {' '}{rel.totals?.[kind]?.paid || 0} creditado(s)
          </span>
          {pend.length > 0 && (
            <Button size="sm" className="ml-auto" data-testid={`gb-final-approve-all-${kind}`}
              onClick={() => acao(`${base}/approve`, { kind }, 'Pagamentos processados.', `all-${kind}`)}
              disabled={busy === `all-${kind}`}>
              {busy === `all-${kind}` ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}
              APROVAR E CREDITAR TODOS ({pend.length})
            </Button>
          )}
        </div>
        {rows.length === 0 && (
          <p className="text-xs text-slate-400" data-testid={`gb-final-${kind}-empty`}>
            Nada congelado ainda.
          </p>
        )}
        <div className="space-y-2">
          {rows.map((r) => {
            const st = STATUS[r.status] || STATUS.AWAITING_APPROVAL;
            return (
              <div key={r.reference} data-testid={`gb-final-row-${r.reference}`}
                className="flex flex-wrap items-center gap-3 rounded-xl border p-2.5">
                {r.rank > 0 && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-black text-amber-700">{r.rank}º</span>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-slate-800">{r.nickname}</p>
                  <p className="text-[11px] text-slate-500">
                    KAKO {r.kako_id} · ref {r.reference}
                    {r.meta?.clan ? ` · ${r.meta.clan} (${r.meta.member_rank}º do clã)` : ''}
                  </p>
                </div>
                <p className="text-sm font-black text-slate-800">{fmt(r.coins)} moedas</p>
                <span className={`flex items-center gap-1 text-[11px] font-bold ${st.c}`}>
                  {r.status === 'PAID' ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                  {st.t}
                </span>
                {r.message && <span className="text-[11px] text-slate-400">{r.message}</span>}
                {r.status !== 'PAID' && r.status !== 'PROCESSING' && (
                  <Button size="sm" className="ml-auto" data-testid={`gb-final-approve-${r.reference}`}
                    onClick={() => acao(`${base}/approve`, { reference: r.reference },
                      'Pagamento processado.', r.reference)}
                    disabled={busy === r.reference}>
                    {busy === r.reference ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                    APROVAR E CREDITAR
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="gb-admin-final">
      <div className="rounded-xl border bg-slate-50 p-3 text-xs text-slate-600">
        No fim do evento o servidor calcula tudo e <b>congela</b>: prêmio fixo do top 3 do ranking de nível,
        prêmio dos clãs (dividido entre os 5 que mais pontuaram em cada clã do top 3) e o <b>saldo de moedas
        amarelas que o cliente deixou na carteira</b> — esse saldo sai da carteira na hora de congelar, então
        ninguém resgata e recebe a devolução duas vezes. <b>Nada é creditado sem a sua aprovação</b> e o bot
        avisa o cliente no chat quando as moedas caem na conta KAKO.
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <div className="rounded-xl border p-3" data-testid="gb-final-balance-total">
          <p className="text-[10px] uppercase text-slate-400">saldo ainda na carteira dos clientes</p>
          <p className="text-base font-black text-amber-600">{fmt(rel.balances?.coins || 0)} moedas</p>
          <p className="text-[11px] text-slate-500">{rel.balances?.players || 0} cliente(s)</p>
        </div>
        <div className="rounded-xl border p-3">
          <p className="text-[10px] uppercase text-slate-400">prêmio do ranking de nível</p>
          <p className="text-base font-black text-slate-700">
            {Object.values(rel.level?.prizes || {}).reduce((a, b) => a + Number(b), 0).toLocaleString('pt-BR')} moedas
          </p>
        </div>
        <div className="rounded-xl border p-3">
          <p className="text-[10px] uppercase text-slate-400">prêmio do ranking de clãs</p>
          <p className="text-base font-black text-slate-700">
            {Object.values(rel.clan?.prizes || {}).reduce((a, b) => a + Number(b), 0).toLocaleString('pt-BR')} moedas
          </p>
        </div>
        <div className="rounded-xl border p-3">
          <p className="text-[10px] uppercase text-slate-400">pagamentos pendentes</p>
          <p className="text-base font-black text-rose-600">{rel.pending || 0}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={load} data-testid="gb-final-reload">
          <RefreshCw className="mr-1 h-3.5 w-3.5" />atualizar
        </Button>
        <Button size="sm" data-testid="gb-final-freeze" disabled={busy === 'freeze'}
          onClick={() => acao(`${base}/freeze`, {},
            'Relatório gerado: prêmios e saldos congelados aguardando aprovação.', 'freeze')}>
          {busy === 'freeze' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Lock className="mr-1 h-3.5 w-3.5" />}
          GERAR RELATÓRIO E CONGELAR
        </Button>
        {seasonStatus === 'active' && (
          <span className="text-[11px] text-amber-700">
            A temporada ainda está ativa — congele só quando o evento terminar.
          </span>
        )}
      </div>

      <div>
        <h4 className="mb-2 text-sm font-bold text-slate-700">Como está o ranking agora</h4>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border p-3" data-testid="gb-final-level-preview">
            <p className="mb-1 flex items-center gap-1.5 text-xs font-black uppercase text-slate-500">
              <Star className="h-3.5 w-3.5" />nível — prêmio fixo
            </p>
            {(rel.level?.winners || []).map((w) => (
              <p key={w.rank} className="text-[12px] text-slate-700">
                <b>{w.rank}º</b> {w.nickname} · nível {w.level} → <b>{fmt(w.coins)}</b> moedas
              </p>
            ))}
            {(rel.level?.winners || []).length === 0 && <p className="text-xs text-slate-400">ninguém pontuou.</p>}
          </div>
          <div className="rounded-xl border p-3" data-testid="gb-final-clan-preview">
            <p className="mb-1 flex items-center gap-1.5 text-xs font-black uppercase text-slate-500">
              <Users className="h-3.5 w-3.5" />clãs — top 5 de cada clã
            </p>
            {(rel.clan?.winners || []).map((c) => (
              <div key={c.clan_id} className="mb-1">
                <p className="text-[12px] font-bold text-slate-700">
                  {c.rank}º [{c.tag}] {c.name} · {fmt(c.points)} pts → {fmt(c.prize)} moedas
                </p>
                {(c.splits || []).map((s) => (
                  <p key={s.kako_id} className="pl-3 text-[11px] text-slate-500">
                    {s.member_rank}º {s.nickname} ({fmt(s.points)} pts) → <b>{fmt(s.coins)}</b>
                  </p>
                ))}
              </div>
            ))}
            {(rel.clan?.winners || []).length === 0 && <p className="text-xs text-slate-400">nenhum clã pontuou.</p>}
          </div>
        </div>
      </div>

      <Lista kind="level" />
      <Lista kind="clan" />
      <Lista kind="balance" />
    </div>
  );
}
